/**
 * @module nhcmap/MapManager
 */
import { Map as olMap, View } from 'ol';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import * as olExtent from 'ol/extent';
import { unByKey } from 'ol/Observable';
import VectorSource from 'ol/source/Vector';
import CoreLayers from './layers/CoreLayers.js';
import ProductManager from './ProductManager.js';
import MapControls from './MapControls.js';
import SelectProductControl from './controls/SelectProductControl.js';
import { getStateAbbrLayer } from './layers/getStateAbbrLayer.js';
import { loadConfig } from './utils/loadUtils.js';
import { initI18n, mergeProductLanguages, t } from './utils/i18n.js';


class MapManager {

  constructor(products = [], params) {
    this.products = products;
    this.productManager = null;
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        this[key] = params[key];
      }
    }
    this.map = null;
    this.autoZoom = params.autoZoom || false;
    this.latOffset = params.latOffset || 0;
    this.displayDropdown = params.displayDropdown || false;
    
    // Set language and make sure it's one we support
    this.languages = ["en", "es"];
    this.language = params.language || 'en';
    if (!this.languages.includes(this.language)) {
      this.language = 'en';
    }

    this.autoZoomState = { // Encapsulate autoZoom-related state
      layerLoadingStatus: new Map(),
      listenerKeys: [],
      hasTriggeredZoom: false,
    };
  }

  static async create(products = [], params = {}) {
    const mapManager = new MapManager(products, params);

    // This loads the language data before any components are created.
    await initI18n(mapManager.language);

    await mapManager.initialize();

    // Load all product configurations
    const configs = await mapManager.loadAllProductConfigs();

    // MERGE all product language files into the i18n system
    await mergeProductLanguages(configs, mapManager.language);

    await mapManager.productManager.addProductsFromConfigs(configs);
    mapManager.mapControls.loadControlsFromConfigs(configs, mapManager);

    if (mapManager.displayDropdown) {
      mapManager.loadSelectProductControl();
    }
    if (mapManager.autoZoom) {
      mapManager.autoZoomAllLayers();
    }
    // Set up periodic updates
    const updateInterval = 5 * 60 * 1000; // 5 minutes in milliseconds
    setInterval(() => mapManager.updateProducts(), updateInterval);
    return mapManager;
  }

  /**
   * Initialize the map
   * @returns {Promise<void>} All async methods return a promise
   */
  async initialize() {

    // Import core layers
    const coreLayers = new CoreLayers(this.language);

    // Create the map
    this.map = new olMap({
      controls: [
        coreLayers.zoomControl,
        coreLayers.attributionControl
      ],
      layers: [
        coreLayers.baseLayerGroup,
        coreLayers.borderLayer,
        coreLayers.labelLayer,
        coreLayers.graticule
      ],
      overlays: [],
      target: 'map',
      view: new View({
        center: fromLonLat([-87.0, 33.9]), // was -82.0, 33.9
        zoom: 4,
        minZoom: 2,
        maxZoom: 10
      }),
    });

    // Add abbreviations for states
    const stateAbbrLayer = getStateAbbrLayer(this.map, true, "16px");
    stateAbbrLayer.set('name', 'stateAbbrLayer');
    this.map.addLayer(stateAbbrLayer);

    this.productManager = new ProductManager(this.map, this.language);
    this.mapControls = new MapControls();
  }

  /**
   * Loads and merges configurations for all active products.
   */
  async loadAllProductConfigs() {
    const promises = this.products
      .filter(p => p.checked)
      .map(p => loadConfig(`/maps/${p.id}/${p.id}.json`, this.language));
    
    return Promise.all(promises);
  }

  /**
   * Loads the product selection control.
   */
  loadSelectProductControl() {
    const control = new SelectProductControl({
      target: document.getElementById('map'),
      mapManager: this,
      products: this.products
    });
    this.map.addControl(control);
  }

  /**
   * Adds a product to the map.
   * @param {string} productId 
   */
  async addProduct(productId) {
    await this.productManager.addProduct(productId);
    this.products.push({ id: productId, name: productId, checked: true });
  }

  /**
   * Removes a product from the map.
   * @param {string} productId 
   */
  async removeProduct(productId) {
    await this.productManager.removeProduct(productId);
    this.products = this.products.filter(id => id !== productId);
  }

  /**
   * Updates all products on the map.
   */
  async updateProducts() {
    console.log("Updating products...");
    for (const product of this.products) {
      if (product.checked) {
        const productId = product.id;
        await this.productManager.updateProduct(productId);
      }
    }
  }

  /**
   * Changes the map's language and reloads all language-dependent layers.
   * @param {string} newLanguage - The new two-letter language code (e.g., 'es').
   * @returns {Promise<void>}
   */
  async changeLanguage(newLanguage) {
    if (!this.languages.includes(newLanguage) || newLanguage === this.language) {
      return; // Do nothing if language is unsupported or unchanged
    }

    this.language = newLanguage;

    // Re-initialize internationalization for the new language
    await initI18n(this.language);
    const configs = await this.loadAllProductConfigs();
    await mergeProductLanguages(configs, this.language);

    // Update the ProductManager's language
    this.productManager.setLanguage(newLanguage);

    // Re-create and replace the core language-dependent layers
    await this.reloadCoreLayers();

    // Tell the ProductManager to reload all currently active products
    await this.productManager.reloadLanguageProducts();

    // Tell the existing controls to update their text (lightweight)
    this.mapControls.updateControlLanguages();
  }

  /**
   * Helper method to reload and replace core layers that depend on language.
   * @private
   */
  async reloadCoreLayers() {

    // 1. Find and remove the old language-dependent controls
    const coreControlNames = ['nhcCoreZoom', 'nhcCoreAttribution'];
    const controlsToRemove = this.map.getControls().getArray()
      .filter(control => coreControlNames.includes(control.get('name')));
    controlsToRemove.forEach(control => this.map.removeControl(control));

    // 2. Find and remove the old language-dependent layers
    const layersToRemove = this.map.getLayers().getArray()
      .filter(layer => ['nhcLabelLayer', 'nhcGraticule'].includes(layer.get('name')));
    layersToRemove.forEach(layer => this.map.removeLayer(layer));

    // Create new instances with the updated language
    const coreLayers = new CoreLayers(this.language);
    this.map.addControl(coreLayers.zoomControl);
    this.map.addControl(coreLayers.attributionControl);
    this.map.addLayer(coreLayers.labelLayer);
    this.map.addLayer(coreLayers.graticule);
  }

  /**
   * Auto-zoom the map to the combined extent of all layers with valid extents.
   */
  autoZoomAllLayers() {
    const skipLayers = ['nhcMapLayer', 'nhcBorderLayer', 'nhcLabelLayer', 'nhcGraticule', 'stateAbbrLayer'];

    const relevantLayers = this.map.getLayers().getArray().filter(layer =>
      !skipLayers.includes(layer.get('name')) &&
      layer.getVisible() &&
      layer.getSource()
    );

    if (relevantLayers.length === 0) {
      console.warn("No relevant layers found to auto-zoom to. Skipping autoZoom.");
      return;
    }

    // Reset state for a new auto-zoom attempt
    this.autoZoomState.layerLoadingStatus = new Map();
    this.autoZoomState.listenerKeys.forEach(key => unByKey(key)); // Clean up old listeners
    this.autoZoomState.listenerKeys = [];
    this.autoZoomState.hasTriggeredZoom = false;

    // Initialize status for all relevant layers
    relevantLayers.forEach(layer => {
      this.autoZoomState.layerLoadingStatus.set(layer, { featuresReady: false, sourceReady: false });
    });

    // Helper to check if ALL relevant layers are ready (features loaded or source ready)
    const areAllLayersReady = () => {
      // Access layerLoadingStatus via 'this.autoZoomState'
      for (const [layer, status] of this.autoZoomState.layerLoadingStatus.entries()) {
        const source = layer.getSource();
        if (source instanceof VectorSource) {
          if (!status.featuresReady || source.getState() !== 'ready') {
            return false;
          }
        } else {
          if (!status.sourceReady || source.getState() !== 'ready') {
            return false;
          }
        }
      }
      return true;
    };

    // Function to perform zoom and cleanup
    const triggerAutoZoom = () => {
      if (this.autoZoomState.hasTriggeredZoom) {
        return;
      }
      this.autoZoomState.hasTriggeredZoom = true;

      this.autoZoomState.listenerKeys.forEach(key => unByKey(key)); // Clean up listeners
      this.autoZoomState.listenerKeys = []; // Clear the array

      this._zoomToCombinedExtent(relevantLayers);
    };

    // --- Set up event listeners for each relevant layer ---
    relevantLayers.forEach(layer => {
      const source = layer.getSource();
      const layerName = layer.get('name') || 'Unnamed Layer';

      if (source instanceof VectorSource) {
        const featuresLoadEndKey = source.on('featuresloadend', (event) => {
          const currentStatus = this.autoZoomState.layerLoadingStatus.get(layer);
          currentStatus.featuresReady = true;
          currentStatus.sourceReady = true; // Implies source ready
          if (areAllLayersReady()) {
            triggerAutoZoom();
          }
        });
        this.autoZoomState.listenerKeys.push(featuresLoadEndKey);

        const featuresLoadErrorKey = source.on('featuresloaderror', () => {
          const currentStatus = this.autoZoomState.layerLoadingStatus.get(layer);
          currentStatus.featuresReady = true;
          currentStatus.sourceReady = true;
          console.error(`Error loading features for vector layer '${layerName}'.`);
          if (areAllLayersReady()) {
            triggerAutoZoom();
          }
        });
        this.autoZoomState.listenerKeys.push(featuresLoadErrorKey);

        if (source.getState() === 'ready' && source.getFeatures().length > 0) {
          const currentStatus = this.autoZoomState.layerLoadingStatus.get(layer);
          currentStatus.featuresReady = true;
          currentStatus.sourceReady = true;
        }

      } else {
        const changeKey = source.on('change', () => {
          if (source.getState() === 'ready') {
            const currentStatus = this.autoZoomState.layerLoadingStatus.get(layer);
            currentStatus.sourceReady = true;
            if (areAllLayersReady()) {
              triggerAutoZoom();
            }
          }
        });
        this.autoZoomState.listenerKeys.push(changeKey);

        if (source.getState() === 'ready') {
          const currentStatus = this.autoZoomState.layerLoadingStatus.get(layer);
          currentStatus.sourceReady = true;
        }
      }
    });

    setTimeout(() => {
      if (areAllLayersReady()) {
        triggerAutoZoom();
      }
    }, 0);
  }


  /**
   * Zooms the map to the combined extent of the given layers.
   * @param {Array} layers - The layers to include in the zoom.
   */
  _zoomToCombinedExtent(layers) {
    let allCoords = [];

    // Gather all coordinates from all layers
    layers.forEach(layer => {
      const source = layer.getSource();
      if (source && typeof source.getFeatures === 'function') {
        source.getFeatures().forEach(feature => {
          const geom = feature.getGeometry();
          // Handle Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon
          let coords = [];
          if (geom.getType() === 'Point') {
            coords = [geom.getCoordinates()];
          } else if (geom.getType() === 'LineString' || geom.getType() === 'MultiPoint') {
            coords = geom.getCoordinates();
          } else if (geom.getType() === 'Polygon' || geom.getType() === 'MultiLineString') {
            coords = geom.getCoordinates().flat();
          } else if (geom.getType() === 'MultiPolygon') {
            coords = geom.getCoordinates().flat(2);
          }
          // Convert all to lon/lat
          coords.forEach(c => {
            const lonLat = toLonLat(c, 'EPSG:3857');
            allCoords.push(lonLat);
          });
        });
      }
    });

    if (allCoords.length === 0) {
      console.warn("No coordinates found for zoom. Skipping auto-zoom.");
      return;
    }

    // Convert back to Web Mercator for extent calculation
    const mercatorCoords = allCoords.map(([lon, lat]) => fromLonLat([lon, lat], 'EPSG:3857'));
    let combinedExtent = olExtent.boundingExtent(mercatorCoords);

    const lonLatExtent = transformExtent(combinedExtent, 'EPSG:3857', 'EPSG:4326');
    const height = Math.abs(lonLatExtent[3] - lonLatExtent[1]);

    let minLon = lonLatExtent[0];
    let maxLon = lonLatExtent[2];

    // If the extent is wider than 180°, it likely crosses the dateline
    if (maxLon - minLon > 180) {
      // Adjust: shift all longitudes < 0 by +360, recalculate min/max
      const allLons = allCoords.map(c => (c[0] < 0 ? c[0] + 360 : c[0]));
      minLon = Math.min(...allLons);
      maxLon = Math.max(...allLons);

      // Build new extent in lon/lat, then transform back to EPSG:3857
      const adjustedLonLatExtent = [minLon, lonLatExtent[1], maxLon, lonLatExtent[3]];
      combinedExtent = transformExtent(adjustedLonLatExtent, 'EPSG:4326', 'EPSG:3857');
    }

    // Calculate scale factor based on height. For heights ≤ 10°, use scale 
    // factor 2.0. For heights ≥ 20°, use scale factor 1.25. For heights between
    // 10° and 20°, interpolate linearly between 2.0 and 1.25. These numbers
    // are educated guesses at this point and may require adjustment.
    let scaleFactor;
    if (height <= 10) {
      scaleFactor = 2.0;
    } else if (height >= 20) {
      scaleFactor = 1.25;
    } else {
      // Linear interpolation between 2.0 and 1.25
      scaleFactor = 2.0 - ((height - 10) / 10) * (2.0 - 1.25);
    }

    if (!olExtent.isEmpty(combinedExtent)) {
      olExtent.scaleFromCenter(combinedExtent, scaleFactor);
      const view = this.map.getView();
      const resolution = view.getResolutionForExtent(combinedExtent);
      const zoom = view.getZoomForResolution(resolution);

      // Get the center in lon/lat, apply latitude offset, then transform back
      let center = olExtent.getCenter(combinedExtent);
      let centerLonLat = toLonLat(center, 'EPSG:3857');
      centerLonLat[1] += this.latOffset; // Shift latitude up
      //let newCenter = fromLonLat(centerLonLat, 'EPSG:3857');

      // Adjust the center to ensure it crosses the dateline correctly
      let currentCenter = toLonLat(view.getCenter(), 'EPSG:3857');
      let targetCenter = centerLonLat.slice(); // already in lon/lat
      // Calculate longitude difference
      let lonDiff = targetCenter[0] - currentCenter[0];
      if (Math.abs(lonDiff) > 180) {
        // Adjust target longitude for shortest path
        if (lonDiff > 0) {
          targetCenter[0] -= 360;
        } else {
          targetCenter[0] += 360;
        }
      }
      let newCenter = fromLonLat(targetCenter, 'EPSG:3857');

      view.animate({
        duration: 700,
        center: newCenter,
        zoom: zoom
      });
    }
  }

}

export default MapManager;